/**
 * DryStep (ydm) feature player (sections/ydm-home-player.liquid). One point is open at a time: its clip
 * plays muted, its progress line fills, and when the clip ends the next point opens, round and round like
 * stories. A point with a photo and no clip stays open for STILL ms.
 *
 * - Nothing loads or plays until the player is on screen, and it stops when it scrolls away or the tab is
 *   hidden. Only the open clip and the next one hold a source, so a phone downloads two clips at most.
 * - Hovering does not hold it: it keeps moving on, and a click on a point opens that one (Jack, 16 October,
 *   after a first build that held the open clip on a loop while hovered).
 * - If the browser will not autoplay (iOS Low Power Mode), the covers step through on the STILL timer.
 * - Phone: the story bars under the clip show where it is; a tap on a bar opens that point, a tap on the
 *   right or left of the clip (or a sideways swipe) moves on or back.
 * - Reduced motion: nothing plays or moves on its own; a tap on a point opens it on its cover.
 * - Nothing changes height as it steps through (assets/ydm-player.css), so the page never moves.
 */
(function () {
  'use strict';

  if (customElements.get('ydm-player')) {
    return;
  }

  var STILL = 5000;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('ydm-player', class extends HTMLElement {
    connectedCallback() {
      this.items = Array.prototype.slice.call(this.querySelectorAll('.ydm-player__item'));
      this.layers = Array.prototype.slice.call(this.querySelectorAll('.ydm-player__layer'));
      this.steps = Array.prototype.slice.call(this.querySelectorAll('.ydm-player__step'));
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.ydm-player__card'));
      if (!this.items.length || this.items.length !== this.layers.length) {
        return;
      }
      this.cur = -1;
      this.inView = false;
      this.blocked = false; // autoplay refused: the covers step through on the timer instead
      this.held = 0; // still timer: fraction done before the last pause
      this.since = 0; // still timer: when it last started running
      this.raf = null;
      this.style.setProperty('--ydm-player-still', STILL / 1000 + 's');

      var self = this;
      this.layers.forEach(function (layer) {
        var v = layer.querySelector('video');
        if (!v) {
          return;
        }
        v.addEventListener('playing', function () {
          if (v.requestVideoFrameCallback) {
            v.requestVideoFrameCallback(function () { layer.classList.add('is-playing'); });
          } else {
            layer.classList.add('is-playing');
          }
          if (self.layers[self.cur] === layer) {
            self.preloadNext();
          }
        });
        v.addEventListener('ended', function () {
          if (self.layers[self.cur] !== layer) {
            return;
          }
          if (self.advancing()) {
            self.open(self.cur + 1);
          }
        });
      });

      this.items.forEach(function (item, i) {
        item.querySelector('.ydm-player__title').addEventListener('click', function () {
          self.open(i);
        });
      });
      this.steps.forEach(function (step, i) {
        step.addEventListener('click', function () {
          self.open(i);
        });
      });

      // Phones: a sideways swipe on the clip steps through, and so does a tap on its right (on) or left
      // third (back); vertical scrolling is left alone
      var media = this.querySelector('.ydm-player__media');
      var sx = 0;
      var sy = 0;
      media.addEventListener('touchstart', function (e) {
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      media.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          self.open(self.cur + (dx < 0 ? 1 : -1));
        } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          var box = media.getBoundingClientRect();
          var x = e.changedTouches[0].clientX - box.left;
          self.open(self.cur + (x < box.width / 3 ? -1 : 1));
        }
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.setRunning(function () { self.inView = entries[0].isIntersecting; });
        }, { threshold: 0.35 }).observe(this);
      } else {
        this.inView = true;
      }
      document.addEventListener('visibilitychange', function () { self.setRunning(function () {}); });

      this.open(0);
    }

    playing() {
      return this.inView && !document.hidden && !reduce;
    }

    advancing() {
      return this.playing();
    }

    // Change state through here so the still timer banks the time it ran before stopping.
    setRunning(change) {
      var was = this.advancing();
      change();
      var now = this.advancing();
      if (was && !now) {
        this.held = this.stillFraction();
        this.since = 0;
      } else if (!was && now) {
        this.since = performance.now();
      }
      this.sync();
    }

    stillFraction() {
      var run = this.since ? (performance.now() - this.since) / STILL : 0;
      return Math.min(1, this.held + run);
    }

    // A cover that was held back (sections/ydm-home-player.liquid) gets its sources now.
    cover(layer) {
      var img = layer && layer.querySelector('img[data-srcset]');
      if (img) {
        img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
      }
    }

    arm(layer) {
      var v = layer.querySelector('video');
      if (v && !v.getAttribute('src')) {
        v.preload = 'auto';
        v.src = v.dataset.src;
      }
      return v;
    }

    rest(layer) {
      var v = layer.querySelector('video');
      layer.classList.remove('is-playing');
      if (v && v.getAttribute('src')) {
        v.pause();
        v.removeAttribute('src');
        v.load();
      }
    }

    open(i) {
      var n = this.items.length;
      i = ((i % n) + n) % n;
      var self = this;
      var next = (i + 1) % n;
      this.cur = i;
      this.cover(this.layers[i]);
      this.items.forEach(function (item, k) {
        var on = k === i;
        item.classList.toggle('is-active', on);
        item.querySelector('.ydm-player__title').setAttribute('aria-current', on ? 'true' : 'false');
        item.style.setProperty('--p', 0);
      });
      this.steps.forEach(function (step, k) {
        step.classList.toggle('is-active', k === i);
        step.classList.toggle('is-done', k < i);
        step.setAttribute('aria-current', k === i ? 'true' : 'false');
        step.style.setProperty('--p', 0);
      });
      this.cards.forEach(function (card, k) {
        card.classList.toggle('is-active', k === i);
      });
      this.layers.forEach(function (layer, k) {
        var on = k === i;
        if (on && layer.classList.contains('ydm-player__layer--still')) {
          var img = layer.querySelector('.ydm-player__poster');
          if (img) {
            img.style.animation = 'none';
            void img.offsetWidth; // restart the slow push-in
            img.style.animation = '';
          }
        }
        layer.classList.toggle('is-active', on);
        var v = layer.querySelector('video');
        if (on) {
          if (v && v.getAttribute('src')) {
            try { v.currentTime = 0; } catch (e) {}
          }
        } else if (k === next) {
          if (v) {
            v.pause(); // the preloaded next clip keeps its source
            layer.classList.remove('is-playing');
          }
        } else {
          self.rest(layer);
        }
      });
      this.held = 0;
      this.since = this.advancing() ? performance.now() : 0;
      this.sync();
    }

    // The next clip loads once the open one is playing, so the change to it is instant.
    preloadNext() {
      var next = this.layers[(this.cur + 1) % this.layers.length];
      this.cover(next);
      if (reduce || this.blocked || !this.playing()) {
        return;
      }
      this.arm(next);
    }

    // Play or pause the open clip to match the player's state, and run the progress line.
    sync() {
      var self = this;
      var layer = this.layers[this.cur];
      if (!layer) {
        return;
      }
      var v = layer.querySelector('video');
      if (v && !this.blocked) {
        if (this.playing()) {
          this.arm(layer);
          v.loop = false;
          var p = v.play();
          if (p && p.catch) {
            p.catch(function () {
              // Autoplay refused (Low Power Mode, data saver): step through the covers instead
              if (self.layers[self.cur] === layer && self.playing() && v.paused) {
                self.blocked = true;
                self.layers.forEach(function (l) { self.rest(l); });
                self.cover(self.layers[(self.cur + 1) % self.layers.length]);
                self.held = 0;
                self.since = self.advancing() ? performance.now() : 0;
                self.tick();
              }
            });
          }
        } else {
          v.pause();
        }
      }
      this.tick();
    }

    tick() {
      var self = this;
      cancelAnimationFrame(this.raf);
      if (reduce) {
        return;
      }
      var step = function () {
        var layer = self.layers[self.cur];
        var item = self.items[self.cur];
        var v = layer.querySelector('video');
        var p;
        if (v && !self.blocked) {
          p = v.duration ? v.currentTime / v.duration : 0;
        } else {
          p = self.stillFraction();
          if (p >= 1 && self.advancing()) {
            self.open(self.cur + 1);
            return;
          }
        }
        item.style.setProperty('--p', p.toFixed(4));
        if (self.steps[self.cur]) {
          self.steps[self.cur].style.setProperty('--p', p.toFixed(4));
        }
        if (self.playing()) {
          self.raf = requestAnimationFrame(step);
        }
      };
      step();
    }
  });
})();
